<script setup lang="ts">
import {ref, computed, onMounted} from 'vue';
import {QDialog} from 'quasar';
import {storeToRefs} from 'pinia';
import NamaCommon from '@/utils/common/NamaCommon.ts';
import NamaURLS from '@/utils/constants/NamaURLS.ts';
import Translator from '@/utils/common/Translator.ts';
import EChartWidget from '@/components/dashboardWidgets/EChartWidget.vue';
import TableDashboardWidget from '@/components/dashboardWidgets/TableDashboardWidget.vue';
import {useCrossFilterStore} from '@/stores/crossFilterStore.ts';
import {useNamaFlagsStore} from '@/stores/nama-flags-store.ts';
import CrossFilterRenderer from '@/components/bi/CrossFilterRenderer.ts';
import DashboardPresenter from '@/utils/presenters/DashboardPresenter.ts';
import type {BIDrillDownClick, BIDrillDownByClick, BIDrillDownByPointValues, BIDrillDownFilterMetadata, BIDrillDownTarget} from '@/components/bi/biChartTypes.ts';
import FieldTypesUtils from '@/utils/common/FieldTypesUtils.ts';
import FieldType from '@/utils/constants/FieldType.ts';
import DTOBIChartLine = namaTypes.DTOBIChartLine;
import DTOFilledQuestionField = namaTypes.DTOFilledQuestionField;
import QuestionField = namaTypes.QuestionField;

const {darkMode} = storeToRefs(useNamaFlagsStore());

interface DrillDownStep {
  mode: 'widgetDrillDown' | 'dimensionDrillBy' | 'dashboardDrillDown';
  // 6.2 widget drill-down fields
  sourceWidgetCode: string;
  targetWidgetId: string;
  targetWidgetCode: string;
  drillDownFilters: DTOFilledQuestionField[];
  filterMetadata: Record<string, BIDrillDownFilterMetadata>;
  targetLabel: string;
  sourceLabel?: string;
  // 6.3 dimension drill-by fields
  drillDownByTargetDimension?: string;
  drillDownByStack?: DTOFilledQuestionField[];
  drillByFilterLabel?: string;
  // dashboard drill-down fields
  targetDashboardId?: string;
}

const props = defineProps<{
  modelValue: boolean;
  dashboardId?: string;
  rootClick?: BIDrillDownClick;
  rootDrillByClick?: BIDrillDownByClick;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>();

const dialogModel = computed<boolean>({
  get: () => props.modelValue,
  set: (v) => emit('update:modelValue', v),
});

const drillStack = ref<DrillDownStep[]>([]);
const currentChartLine = ref<DTOBIChartLine | undefined>();
const dashboardChartLines = ref<DTOBIChartLine[]>([]);
const loading = ref<boolean>(false);
const errorMessage = ref<string | undefined>();
const chartKey = ref<number>(0);
const maximized = ref<boolean>(false);
const isDashboardMode = computed(() => {
  const step = drillStack.value[drillStack.value.length - 1];
  return step?.mode === 'dashboardDrillDown';
});

function toggleMaximize() {
  maximized.value = !maximized.value;
}

onMounted(async () => {
  if (props.rootClick) {
    drillStack.value = [buildStepFromClick(props.rootClick)];
  } else if (props.rootDrillByClick) {
    drillStack.value = [buildStepFromDrillByClick(props.rootDrillByClick)];
  }
  await fetchCurrent();
});

function buildStepFromClick(click: BIDrillDownClick): DrillDownStep {
  if (click.target.targetType === 'dashboard') {
    return {
      mode: 'dashboardDrillDown',
      sourceWidgetCode: click.sourceWidgetCode,
      targetWidgetId: '',
      targetWidgetCode: '',
      targetDashboardId: click.target.targetDashboardId,
      drillDownFilters: click.drillDownFilters.map(f => ({id: f.id, value: f.value, sourceChart: f.sourceChart})),
      filterMetadata: click.filterMetadata,
      targetLabel: drillDownTitle(click.target),
      sourceLabel: click.sourceLabel,
    };
  }
  return {
    mode: 'widgetDrillDown',
    sourceWidgetCode: click.sourceWidgetCode,
    targetWidgetId: click.target.targetWidgetId ?? '',
    targetWidgetCode: click.target.targetWidgetCode ?? '',
    drillDownFilters: click.drillDownFilters.map(f => ({id: f.id, value: f.value})),
    filterMetadata: click.filterMetadata,
    targetLabel: drillDownTitle(click.target),
    sourceLabel: click.sourceLabel,
  };
}

function serializeDrillByValue(paramType: string, pointValues: BIDrillDownByPointValues | undefined): string {
  if (!pointValues) return '';
  if ((paramType === FieldType.Reference || paramType === FieldType.Genericreference) && pointValues.id) {
    return FieldTypesUtils.toString(paramType, {
      id: pointValues.id,
      entityType: pointValues.entityType ?? '',
      code: pointValues.code ?? '',
      actualCode: '',
      name1: pointValues.name1 ?? '',
      name2: pointValues.name2 ?? '',
    });
  }
  return pointValues.value ?? '';
}

function buildDrillByStackFromClick(click: BIDrillDownByClick, previousStack?: DTOFilledQuestionField[]): DTOFilledQuestionField[] {
  const stack: DTOFilledQuestionField[] = previousStack ? [...previousStack] : [];
  if (click.currentDimensionFieldId && click.currentDimensionValue) {
    const serialized = serializeDrillByValue(click.currentDimensionParamType, click.currentDimensionValue);
    stack.push({id: click.currentDimensionFieldId, value: serialized, sourceChart: click.sourceWidgetId});
  }
  return stack;
}

function renderDrillByFilterLabel(click: BIDrillDownByClick): string | undefined {
  const val = click.currentDimensionValue;
  if (!val) return undefined;
  const isRef = click.currentDimensionParamType === FieldType.Reference
    || click.currentDimensionParamType === FieldType.Genericreference;
  if (isRef) {
    const name = Translator.isArabic() ? (val.name1 || val.name2) : (val.name2 || val.name1);
    return name || val.code || '';
  }
  return val.value ?? '';
}

function buildStepFromDrillByClick(click: BIDrillDownByClick, previousStack?: DTOFilledQuestionField[]): DrillDownStep {
  return {
    mode: 'dimensionDrillBy',
    sourceWidgetCode: click.sourceWidgetCode,
    targetWidgetId: click.sourceWidgetId,
    targetWidgetCode: click.sourceWidgetCode,
    drillDownFilters: [],
    filterMetadata: {},
    targetLabel: click.dimensionLabel,
    drillDownByTargetDimension: click.targetDimensionFieldId,
    drillDownByStack: buildDrillByStackFromClick(click, previousStack),
    drillByFilterLabel: renderDrillByFilterLabel(click),
  };
}

async function fetchCurrent() {
  const step = drillStack.value[drillStack.value.length - 1];
  if (!step) return;
  loading.value = true;
  errorMessage.value = undefined;
  dashboardChartLines.value = [];
  currentChartLine.value = undefined;
  try {
    if (step.mode === 'dashboardDrillDown') {
      maximized.value = true;
      const presenter = new DashboardPresenter();
      presenter.prefilledQuestions = step.drillDownFilters;
      await presenter.fetchCharts(step.targetDashboardId);
      dashboardChartLines.value = (presenter.fetchedData.value?.chartLines ?? []) as DTOBIChartLine[];
      chartKey.value++;
    } else {
      const crossFilterStore = useCrossFilterStore();
      const requestData: Record<string, unknown> = {
        id: props.dashboardId || '',
        widgetId: step.targetWidgetId,
        activeCrossFilters: crossFilterStore.getFilledQuestionFields(),
      };

      if (step.mode === 'widgetDrillDown') {
        requestData.drillDownSourceWidgetCode = step.sourceWidgetCode;
        requestData.drillDownFilters = step.drillDownFilters;
      } else if (step.mode === 'dimensionDrillBy') {
        requestData.drillDownByTargetDimension = step.drillDownByTargetDimension;
        requestData.drillDownByStack = step.drillDownByStack ?? [];
      }

      const response = await NamaCommon.namaFetch(NamaURLS.FetchSingleBIWidget, {data: requestData});
      if (response?.result?.succeded) {
        currentChartLine.value = response.data as DTOBIChartLine;
        chartKey.value++;
      } else {
        errorMessage.value = Translator.translate('biFailedToLoadDrillDown');
      }
    }
  } catch (e) {
    errorMessage.value = (e as Error)?.message || 'Error';
  } finally {
    loading.value = false;
  }
}

function drillDownTitle(target: BIDrillDownTarget): string {
  const isArabic = Translator.isArabic();
  return (isArabic ? target.arTitle : target.enTitle) || target.enTitle || target.arTitle || '';
}

function currentTitle(): string {
  if (!currentChartLine.value) return '';
  return Translator.isArabic()
    ? (currentChartLine.value.arChartTitle || currentChartLine.value.enChartTitle || '')
    : (currentChartLine.value.enChartTitle || currentChartLine.value.arChartTitle || '');
}

function buildBreadcrumbLabel(step: DrillDownStep): string {
  if (step.mode === 'dimensionDrillBy') {
    if (step.drillByFilterLabel)
      return step.drillByFilterLabel + ' → ' + step.targetLabel;
    return step.targetLabel;
  }
  const parts: string[] = [];
  for (const filter of step.drillDownFilters) {
    const metadata = step.filterMetadata[filter.id ?? ''];
    if (!metadata) continue;
    const filterTitle = Translator.isArabic() ? metadata.arTitle : metadata.enTitle;
    const questionField = {
      id: filter.id,
      fieldType: metadata.paramType,
      metaData: {
        enumTypeName: metadata.enumTypeName,
        referencedEntityType: metadata.referencedEntityType,
      }
    } as QuestionField;
    const rendered = CrossFilterRenderer.renderValue(filter, questionField);
    if (rendered)
      parts.push(filterTitle ? `${filterTitle}: ${rendered}` : rendered);
    else if (filterTitle)
      parts.push(filterTitle);
  }
  if (parts.length)
    return parts.join(' | ');
  return step.targetLabel;
}

async function onNestedDrillDown(click: BIDrillDownClick) {
  drillStack.value.push(buildStepFromClick(click));
  await fetchCurrent();
}

async function onNestedDrillDownBy(click: BIDrillDownByClick) {
  const currentStep = drillStack.value[drillStack.value.length - 1];
  const previousStack = currentStep?.mode === 'dimensionDrillBy' ? currentStep.drillDownByStack : undefined;
  drillStack.value.push(buildStepFromDrillByClick(click, previousStack));
  await fetchCurrent();
}

async function goToBreadcrumb(index: number) {
  if (index >= drillStack.value.length - 1) return;
  drillStack.value = drillStack.value.slice(0, index + 1);
  await fetchCurrent();
}

function widgetComponent(line: DTOBIChartLine | undefined) {
  if (!line) return EChartWidget;
  return line.chartType === 'Table' ? TableDashboardWidget : EChartWidget;
}
</script>

<template>
  <q-dialog v-model="dialogModel" :maximized="maximized">
    <q-card :dark="darkMode" class="drill-down-card"
            :class="[
              darkMode ? 'tw-bg-namaBackground-bright-dark tw-text-gray-100' : 'tw-bg-namaBackground-bright-light tw-text-gray-800',
              maximized ? 'drill-down-card--maximized' : 'drill-down-card--resizable'
            ]">
      <q-card-section class="tw-flex tw-items-center tw-gap-2 tw-border-b"
                      :class="darkMode ? 'tw-border-gray-600' : 'tw-border-gray-200'">
        <q-icon name="analytics" size="20px" class="tw-text-primary"/>
        <div class="tw-text-sm tw-font-semibold tw-grow">{{ currentTitle() }}</div>
        <q-btn flat round dense :icon="maximized ? 'fullscreen_exit' : 'fullscreen'" @click="toggleMaximize"/>
        <q-btn flat round dense icon="close" @click="dialogModel = false"/>
      </q-card-section>
      <q-card-section class="tw-py-2 tw-border-b"
                      :class="darkMode ? 'tw-border-gray-600 tw-bg-gray-800' : 'tw-border-gray-200 tw-bg-gray-50'">
        <q-breadcrumbs :dark="darkMode" class="tw-text-xs">
          <q-breadcrumbs-el v-for="(step, index) in drillStack"
                            :key="index"
                            :label="buildBreadcrumbLabel(step)"
                            :class="{'tw-cursor-pointer tw-text-primary': index < drillStack.length - 1}"
                            @click="goToBreadcrumb(index)"/>
        </q-breadcrumbs>
      </q-card-section>
      <q-card-section class="drill-down-body"
                      :class="darkMode ? 'tw-bg-namaBackground-bright-dark' : 'tw-bg-namaBackground-bright-light'">
        <div v-if="loading" class="tw-flex tw-items-center tw-justify-center tw-h-full">
          <q-spinner size="40px" color="primary"/>
        </div>
        <div v-else-if="errorMessage" class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-full">
          <q-icon name="warning" color="orange" size="2rem"/>
          <p class="tw-text-sm tw-mt-2" :class="darkMode ? 'tw-text-gray-400' : 'tw-text-gray-600'">{{ errorMessage }}</p>
        </div>
        <template v-else-if="isDashboardMode && dashboardChartLines.length">
          <div class="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-3 tw-p-2 tw-h-full tw-overflow-auto">
            <div v-for="(line, idx) in dashboardChartLines" :key="idx"
                 class="tw-min-h-[300px] tw-border tw-border-gray-200 dark:tw-border-gray-600 tw-rounded">
              <component :is="widgetComponent(line)"
                         :chartLine="line"
                         :chartKey="String(chartKey + '-' + idx)"
                         :dashboardId="dashboardId"
                         @drillDownRequested="onNestedDrillDown"
                         @drillDownByRequested="onNestedDrillDownBy"/>
            </div>
          </div>
        </template>
        <component v-else-if="currentChartLine"
                   :is="widgetComponent(currentChartLine)"
                   :chartLine="currentChartLine"
                   :chartKey="String(chartKey)"
                   :dashboardId="dashboardId"
                   @drillDownRequested="onNestedDrillDown"
                   @drillDownByRequested="onNestedDrillDownBy"/>
      </q-card-section>
    </q-card>
  </q-dialog>
</template>

<style scoped>
.drill-down-card {
  display: flex;
  flex-direction: column;
}

.drill-down-card--resizable {
  width: 90vw;
  max-width: 95vw;
  height: 80vh;
  max-height: 95vh;
  resize: both;
  overflow: auto;
}

.drill-down-card--maximized {
  width: 100%;
  height: 100%;
}

.drill-down-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}
</style>
